import { useAuth } from "@bcmi-labs/art-auth"
import { useQuery } from "@tanstack/react-query"
import { useMemo } from "react"

import { useRole } from "@/api/hooks/useRole"
import { useSpace } from "@/api/hooks/useSpace"
import { query } from "@/api/query"
import { MY_SPACE } from "@/constants/spaces"
import {
  getPlanUpgradeLink,
  getRestrictionsRecap,
  getUserType,
  isPlanExpired as isPlanExpiredFn
} from "@/lib/utils/space"

/*
 * ============================================================================
 *
 * NOTE ABOUT THIS HOOK:
 *
 * This hook combines data from multiple sources (space and user subscriptions)
 * As such, it might be wiser to keep all of this inside a query store in the future,
 * but for now this implementation will do.
 *
 * // @freshgiammi - 20/01/2026
 * ============================================================================
 */

/** A hook that returns the user recap from the query store as a stateful value. */
export function useRecap() {
  const { user } = useAuth()
  const { space } = useSpace()
  const { can } = useRole()

  // Check if we're on an expired pro plan
  const isPlanExpired = isPlanExpiredFn(space)

  /**
   * Get recap for the current space
   */
  const spaceRecap = useQuery({
    ...query.space.subscriptions(space.id),
    staleTime: Infinity
  })

  /**
   * Get recap for the current user
   */
  const userRecap = useQuery({
    ...query.space.subscriptions(MY_SPACE.id),
    staleTime: Infinity
  })

  // Use the pure function to compute all restrictions and usage info
  const restrictionsRecap = useMemo(
    () => getRestrictionsRecap(spaceRecap.data, userRecap.data, isPlanExpired),
    [spaceRecap.data, userRecap.data, isPlanExpired]
  )

  /**
   * Plan seats info, Infinity if no plan is set.
   */
  const planSeats = (() => {
    const { plan_id, plan_seats, members } = space

    let limit = Infinity

    if (plan_seats && plan_seats !== 0) {
      limit = plan_seats
    } else if (space.space === "pro") {
      limit = spaceRecap.data?.limits.cloud_things === 100 ? 50 : Infinity
    } else if (!plan_id) {
      limit = Infinity
    }

    return {
      limit,
      available: limit - (members || 0)
    }
  })()

  const userType = getUserType(space.roles)

  // Gated centrally: consumers rely on `upgradeLink && ...` to hide upsell CTAs for
  // roles that can't act on billing.
  const upgradeLink = can("organization::update-billing")
    ? getPlanUpgradeLink(space, user["http://arduino.cc/is_minor"] || false, spaceRecap.data?.plans || [])
    : undefined

  // ? Does this need a refactoring to group all the return values?
  return {
    userRecap,
    spaceRecap,
    ...restrictionsRecap,
    isPlanExpired,
    planSeats,
    /** User type (admin, teacher, student, editor) */
    userType,
    /** Upgrade link for the current space */
    upgradeLink
  }
}
